<script setup lang="ts">
import { ref, onMounted } from "vue";
import {
  ScheduleDependOnResult,
  createTask,
  deferred
} from "@zyzgroup/core-common";
import { TWEEN, type EasingFunction } from "@zyzgroup/core-web";
import EaseGraphDisplay from "./EaseGraphDisplay.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { EaseGraphDisplay },
  inheritAttrs: true
});

const defaultEaseDuration = 2000;

const props = withDefaults(
  defineProps<{
    easeList?: {
      easeName: string;
      easeFunction?: EasingFunction;
      easeDuration?: number;
    }[];
  }>(),
  {
    easeList: () => [
      {
        easeName: "Linear",
        easeFunction: TWEEN.Easing.Linear.None
      },
      {
        easeName: "QuadraticIn",
        easeFunction: TWEEN.Easing.Quadratic.In
      },
      {
        easeName: "QuadraticOut",
        easeFunction: TWEEN.Easing.Quadratic.Out
      },
      {
        easeName: "QuadraticInOut",
        easeFunction: TWEEN.Easing.Quadratic.InOut
      },
      {
        easeName: "CubicIn",
        easeFunction: TWEEN.Easing.Cubic.In
      },
      {
        easeName: "CubicOut",
        easeFunction: TWEEN.Easing.Cubic.Out
      },
      {
        easeName: "CubicInOut",
        easeFunction: TWEEN.Easing.Cubic.InOut
      },
      {
        easeName: "QuarticIn",
        easeFunction: TWEEN.Easing.Quartic.In
      },
      {
        easeName: "QuarticOut",
        easeFunction: TWEEN.Easing.Quartic.Out
      },
      {
        easeName: "QuarticInOut",
        easeFunction: TWEEN.Easing.Quartic.InOut
      },
      {
        easeName: "QuinticIn",
        easeFunction: TWEEN.Easing.Quintic.In
      },
      {
        easeName: "QuinticOut",
        easeFunction: TWEEN.Easing.Quintic.Out
      },
      {
        easeName: "QuinticInOut",
        easeFunction: TWEEN.Easing.Quintic.InOut
      },
      {
        easeName: "SinusoidalIn",
        easeFunction: TWEEN.Easing.Sinusoidal.In
      },
      {
        easeName: "SinusoidalOut",
        easeFunction: TWEEN.Easing.Sinusoidal.Out
      },
      {
        easeName: "SinusoidalInOut",
        easeFunction: TWEEN.Easing.Sinusoidal.InOut
      },
      {
        easeName: "ExponentialIn",
        easeFunction: TWEEN.Easing.Exponential.In
      },
      {
        easeName: "ExponentialOut",
        easeFunction: TWEEN.Easing.Exponential.Out
      },
      {
        easeName: "ExponentialInOut",
        easeFunction: TWEEN.Easing.Exponential.InOut
      },
      {
        easeName: "CircularIn",
        easeFunction: TWEEN.Easing.Circular.In
      },
      {
        easeName: "CircularOut",
        easeFunction: TWEEN.Easing.Circular.Out
      },
      {
        easeName: "CircularInOut",
        easeFunction: TWEEN.Easing.Circular.InOut
      },
      {
        easeName: "ElasticIn",
        easeFunction: TWEEN.Easing.Elastic.In
      },
      {
        easeName: "ElasticOut",
        easeFunction: TWEEN.Easing.Elastic.Out
      },
      {
        easeName: "ElasticInOut",
        easeFunction: TWEEN.Easing.Elastic.InOut
      },
      {
        easeName: "BackIn",
        easeFunction: TWEEN.Easing.Back.In
      },
      {
        easeName: "BackOut",
        easeFunction: TWEEN.Easing.Back.Out
      },
      {
        easeName: "BackInOut",
        easeFunction: TWEEN.Easing.Back.InOut
      },
      {
        easeName: "BounceIn",
        easeFunction: TWEEN.Easing.Bounce.In
      },
      {
        easeName: "BounceOut",
        easeFunction: TWEEN.Easing.Bounce.Out
      },
      {
        easeName: "BounceInOut",
        easeFunction: TWEEN.Easing.Bounce.InOut
      },
      {
        easeName: "generatePow(10)In",
        easeFunction: TWEEN.Easing.generatePow(10).In
      },
      {
        easeName: "generatePow(10)Out",
        easeFunction: TWEEN.Easing.generatePow(10).Out
      },
      {
        easeName: "generatePow(10)InOut",
        easeFunction: TWEEN.Easing.generatePow(10).InOut
      },
      {
        easeName: "4 steps",
        easeFunction: TWEEN.Easing.createStep(4)
      },
      {
        easeName: "8 steps",
        easeFunction: TWEEN.Easing.createStep(8)
      },
      {
        easeName: "12 steps",
        easeFunction: TWEEN.Easing.createStep(12)
      },
      {
        easeName: "16 steps",
        easeFunction: TWEEN.Easing.createStep(16)
      },
      {
        easeName: "Noisy Exponential.InOut",
        easeFunction: TWEEN.Easing.createNoisyEasing(
          TWEEN.Easing.Exponential.InOut,
          0.1
        )
      },
      {
        easeName: "Noisy Elastic.InOut",
        easeFunction: TWEEN.Easing.createNoisyEasing(
          TWEEN.Easing.Elastic.InOut,
          0.2
        )
      },
      {
        easeName: "Noisy Circular.InOut",
        easeFunction: TWEEN.Easing.createNoisyEasing(
          TWEEN.Easing.Circular.InOut,
          0.1
        )
      },
      {
        easeName: "swing",
        easeFunction: TWEEN.Easing.swing
      },
      {
        easeName: "causeSeizures",
        easeFunction: TWEEN.Easing.causeSeizures
      }
    ]
  }
);

const emits = defineEmits<{
  (
    event: "selectedEaseFunction",
    easeName: string,
    easeFunction: EasingFunction
  ): void;
}>();

const children: Record<string, any> = {};
function setChildRef(el: any, easeName: string) {
  if (el) {
    children[easeName] = { el, defer: deferred() };
  }
}

// 初次加载全部执行一遍
const schedule = new ScheduleDependOnResult({ concurrency: 10 });
function onMount(easeName: string) {
  schedule.asap(
    createTask({
      fn: () => {
        children[easeName].el.start();
        return children[easeName].defer.promise;
      }
    })
  );
}

function onComplete(easeName: string) {
  const child = children[easeName];
  if (child) {
    child.defer.resolve();
    delete children[easeName];
  }
}

const selectedEaseName = ref("Linear");
function onSelect(easeName: string, easeFunction: EasingFunction) {
  selectedEaseName.value = easeName;
  emits("selectedEaseFunction", easeName, easeFunction);
}

onMounted(() => {
  onSelect("Linear", TWEEN.Easing.Linear.None);
});

defineExpose({});
</script>

<template>
  <div>
    <template
      v-for="ease in props.easeList"
      :key="ease.easeName"
    >
      <div class="ease-graph-display-wrap">
        <EaseGraphDisplay
          v-if="ease.easeFunction"
          :easeName="ease.easeName"
          :easeFunction="ease.easeFunction"
          :easeDuration="ease.easeDuration || defaultEaseDuration"
          :isSelected="selectedEaseName == ease.easeName"
          @mount="onMount"
          @select="onSelect"
          @complete="onComplete"
          :ref="(el) => setChildRef(el, ease.easeName)"
        />
      </div>
    </template>
  </div>
</template>

<!--
  ce 中不要使用v-bind
  color: v-bind(fontColor);
-->
<style scoped>
.ease-graph-display-wrap {
  display: inline-block;
  margin-right: 10px;
}
</style>
